import { useState, useEffect } from "react";
import { Edit3, MapPin, Loader2, Save, RefreshCw } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { updateGarden } from "@/lib/api";
import LocationPickerMap from "@/components/LocationPickerMap";

/**
 * Top-of-page card showing garden details with a "Edit" dialog.
 */
export function GardenInfoCard({ garden, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!garden) return;
        setForm({
            name: garden.name || "",
            location: garden.location || "",
            climate: garden.climate || "",
            latitude: garden.latitude ?? 0,
            longitude: garden.longitude ?? 0,
            length_cm: garden.length_cm ?? 1000,
            width_cm: garden.width_cm ?? 800,
            notes: garden.notes || "",
        });
    }, [garden]);

    const submit = async () => {
        setSaving(true);
        try {
            await updateGarden(garden.id, {
                name: form.name,
                location: form.location,
                climate: form.climate,
                latitude: Number(form.latitude),
                longitude: Number(form.longitude),
                length_cm: Number(form.length_cm),
                width_cm: Number(form.width_cm),
                notes: form.notes || null,
            });
            toast.success("Garden details updated");
            onSaved?.();
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (!garden) return null;

    return (
        <div className="journal-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="garden-info-card">
            <div>
                <div className="overline text-[var(--vm-sage)] mb-1">Garden</div>
                <h2 className="font-serif-display text-3xl text-[var(--vm-text)]">{garden.name}</h2>
                <div className="text-sm text-[var(--vm-text-secondary)] mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {garden.location}</span>
                    <span>{garden.climate}</span>
                    <span className="font-mono-grid text-xs text-[var(--vm-text-muted)]">
                        {(garden.length_cm / 100).toFixed(1)} × {(garden.width_cm / 100).toFixed(1)} m
                    </span>
                </div>
                {garden.notes && (
                    <div className="text-xs italic text-[var(--vm-text-muted)] mt-2 max-w-xl">{garden.notes}</div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button variant="ghost" data-testid="edit-garden-btn">
                        <Edit3 className="w-4 h-4 mr-2" /> Edit garden
                    </Button>
                </DialogTrigger>
                <DialogContent className="bg-[var(--vm-card)] border-[var(--vm-border)] max-w-lg" data-testid="garden-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif-display text-2xl">Garden details</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
                        <div className="grid gap-2">
                            <Label>Name</Label>
                            <Input data-testid="garden-name" value={form.name}
                                   onChange={e => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-2">
                                <Label>Location</Label>
                                <Input data-testid="garden-location" value={form.location}
                                       onChange={e => setForm({ ...form, location: e.target.value })} />
                            </div>
                            <div className="grid gap-2">
                                <Label>Climate</Label>
                                <Input data-testid="garden-climate" value={form.climate}
                                       onChange={e => setForm({ ...form, climate: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <div className="flex items-center justify-between">
                                <Label>Pin location on map</Label>
                                <button
                                    type="button"
                                    data-testid="garden-locate-me-btn"
                                    onClick={() => {
                                        if (!navigator.geolocation) {
                                            toast.error("Geolocation not available");
                                            return;
                                        }
                                        navigator.geolocation.getCurrentPosition(
                                            (pos) => setForm((f) => ({
                                                ...f,
                                                latitude: pos.coords.latitude.toFixed(4),
                                                longitude: pos.coords.longitude.toFixed(4),
                                            })),
                                            (err) => toast.error(err.message || "Could not detect location")
                                        );
                                    }}
                                    className="text-xs text-[var(--vm-accent)] hover:underline flex items-center gap-1"
                                >
                                    <RefreshCw className="w-3 h-3" /> use my current location
                                </button>
                            </div>
                            <LocationPickerMap
                                lat={Number(form.latitude)}
                                lon={Number(form.longitude)}
                                onChange={(la, lo) => setForm({
                                    ...form,
                                    latitude: la.toFixed(4),
                                    longitude: lo.toFixed(4),
                                })}
                                height={240}
                            />
                            <p className="text-[11px] text-[var(--vm-text-muted)]">
                                Click anywhere on the map or drag the marker. Weather will refresh automatically after saving.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-2">
                                <Label>Latitude</Label>
                                <Input type="number" step="0.0001" data-testid="garden-lat" value={form.latitude}
                                       onChange={e => setForm({ ...form, latitude: e.target.value })} />
                            </div>
                            <div className="grid gap-2">
                                <Label>Longitude</Label>
                                <Input type="number" step="0.0001" data-testid="garden-lon" value={form.longitude}
                                       onChange={e => setForm({ ...form, longitude: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-2">
                                <Label>Length (cm)</Label>
                                <Input type="number" min="100" data-testid="garden-length" value={form.length_cm}
                                       onChange={e => setForm({ ...form, length_cm: e.target.value })} />
                            </div>
                            <div className="grid gap-2">
                                <Label>Width (cm)</Label>
                                <Input type="number" min="100" data-testid="garden-width" value={form.width_cm}
                                       onChange={e => setForm({ ...form, width_cm: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label>Notes</Label>
                            <Textarea data-testid="garden-notes" value={form.notes}
                                      onChange={e => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={submit} disabled={saving} data-testid="garden-save"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                            {saving
                                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                                : <><Save className="w-4 h-4 mr-2" /> Save</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
